import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { Permission, getRolePermissions } from '../services/permissions';

interface Collaborator {
  id: string;
  user_id: string;
  added_by: string;
  custom_role?: string;
  permissions: string[];
  sections_access: string[];
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  added_by_user?: {
    id: string;
    name: string;
  };
}

const SECTIONS = [
  { id: 'dashboard', label: 'Tableau de bord' },
  { id: 'projects', label: 'Projets' },
  { id: 'reporting', label: 'Rapports' },
  { id: 'data_collection', label: 'Collecte de données' },
  { id: 'communication', label: 'Communication' },
  { id: 'inventory', label: 'Inventaire' },
  { id: 'knowledge', label: 'Base de connaissances' },
  { id: 'kpis', label: 'KPIs' },
  { id: 'users', label: 'Gestion utilisateurs' },
];

const CollaboratorsManagement: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, isAgence } = usePermissions();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<string | null>(null);
  const [editingCollaborator, setEditingCollaborator] = useState<Collaborator | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    user_id: '',
    custom_role: '',
    permissions: [] as string[],
    sections_access: [] as string[],
    notes: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [collaboratorsRes, usersRes] = await Promise.all([
        api.collaborators.getAll(),
        api.users.getAll(),
      ]);
      setCollaborators(collaboratorsRes.collaborators || []);
      setUsers(usersRes.users || []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingCollaborator(null);
    setFormData({
      user_id: '',
      custom_role: '',
      permissions: [],
      sections_access: [],
      notes: '',
      is_active: true,
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (collaborator: Collaborator) => {
    setEditingCollaborator(collaborator);
    setFormData({
      user_id: collaborator.user_id,
      custom_role: collaborator.custom_role || '',
      permissions: collaborator.permissions || [],
      sections_access: collaborator.sections_access || [],
      notes: collaborator.notes || '',
      is_active: collaborator.is_active,
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.user_id) {
      alert('Veuillez sélectionner un utilisateur');
      return;
    }

    try {
      if (editingCollaborator) {
        await api.collaborators.update(editingCollaborator.id, {
          custom_role: formData.custom_role || undefined,
          permissions: formData.permissions,
          sections_access: formData.sections_access,
          notes: formData.notes || undefined,
          is_active: formData.is_active,
        });
      } else {
        await api.collaborators.create({
          user_id: formData.user_id,
          custom_role: formData.custom_role || undefined,
          permissions: formData.permissions,
          sections_access: formData.sections_access,
          notes: formData.notes || undefined,
        });
      }
      setOpenDialog(false);
      setEditingCollaborator(null);
      loadData();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur: ' + (error.message || 'Une erreur est survenue'));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.collaborators.delete(id);
      setOpenDeleteDialog(null);
      loadData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur: ' + (error.message || 'Une erreur est survenue'));
    }
  };

  const togglePermission = (permission: string) => {
    setFormData({
      ...formData,
      permissions: formData.permissions.includes(permission)
        ? formData.permissions.filter(p => p !== permission)
        : [...formData.permissions, permission],
    });
  };

  const toggleSection = (sectionId: string) => {
    setFormData({
      ...formData,
      sections_access: formData.sections_access.includes(sectionId)
        ? formData.sections_access.filter(s => s !== sectionId)
        : [...formData.sections_access, sectionId],
    });
  };

  if (!isAdmin && !isAgence) {
    return (
      <Box>
        <Alert severity="error">
          Accès refusé. Seuls les administrateurs et l'agence peuvent gérer les collaborateurs.
        </Alert>
      </Box>
    );
  }

  const availableUsers = users.filter(u => 
    !collaborators.some(c => c.user_id === u.id) || 
    (editingCollaborator && editingCollaborator.user_id === u.id)
  );

  const allPermissions: Permission[] = [
    'reports:create', 'reports:submit', 'reports:view_all', 'reports:edit', 'reports:delete', 'reports:pre_validate',
    'reports:approve', 'reports:reject', 'reports:add_recommendation',
    'projects:view_assigned', 'projects:view_all', 'projects:create', 'projects:edit', 'projects:delete',
    'projects:manage', 'projects:validate_milestones', 'projects:archive',
    'surveys:respond', 'surveys:create', 'surveys:delete', 'surveys:deploy', 'surveys:view_analytics',
    'surveys:export', 'surveys:view_readonly',
    'chat:participate', 'chat:create_rooms', 'chat:manage_rooms',
    'inventory:scan', 'inventory:declare', 'inventory:manage', 'inventory:view_all',
    'inventory:view_strategic',
    'knowledge:view_essential', 'knowledge:upload', 'knowledge:manage', 'knowledge:approve',
    'kpis:view_own', 'kpis:define_operational', 'kpis:view_all', 'kpis:define_strategic',
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestion des Collaborateurs</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Ajouter un collaborateur
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Les collaborateurs peuvent avoir des rôles personnalisés et des permissions spécifiques différentes de leur rôle principal.
      </Alert>

      {/* Liste des collaborateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Rôle principal</TableCell>
              <TableCell>Rôle personnalisé</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Sections</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collaborators.map((collaborator) => (
              <TableRow key={collaborator.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar>
                      {collaborator.user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {collaborator.user?.name || 'Utilisateur inconnu'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {collaborator.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={collaborator.user?.role || '-'} size="small" />
                </TableCell>
                <TableCell>
                  {collaborator.custom_role ? (
                    <Chip label={collaborator.custom_role} size="small" color="primary" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {collaborator.permissions?.length || 0} permission(s)
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {collaborator.sections_access?.length || 0} section(s)
                  </Typography>
                </TableCell>
                <TableCell>
                  {collaborator.is_active ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Actif"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon />}
                      label="Inactif"
                      size="small"
                      color="default"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(collaborator)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setOpenDeleteDialog(collaborator.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {collaborators.length === 0 && (
              <TableRow>
                {/* @ts-ignore - colSpan is valid HTML attribute */}
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun collaborateur pour le moment
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Création/Modification */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCollaborator ? 'Modifier le collaborateur' : 'Ajouter un collaborateur'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Informations générales" />
            <Tab label="Permissions" />
            <Tab label="Accès aux sections" />
          </Tabs>

          {tabValue === 0 && (
            <Box>
              <FormControl fullWidth margin="normal">
                <InputLabel>Utilisateur</InputLabel>
                <Select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  disabled={!!editingCollaborator}
                >
                  {availableUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name} ({u.email}) - {u.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Rôle personnalisé (optionnel)"
                margin="normal"
                value={formData.custom_role}
                onChange={(e) => setFormData({ ...formData, custom_role: e.target.value })}
                placeholder="Ex: Chef de projet, Analyste, etc."
              />
              <TextField
                fullWidth
                label="Notes"
                margin="normal"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Actif"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionnez les permissions à accorder au collaborateur
              </Typography>
              <Grid container spacing={2}>
                {allPermissions.map((permission) => (
                  <Grid item xs={12} sm={6} md={4} key={permission}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.permissions.includes(permission)}
                          onChange={() => togglePermission(permission)}
                        />
                      }
                      label={permission}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionnez les sections du site auxquelles le collaborateur aura accès
              </Typography>
              <FormGroup>
                {SECTIONS.map((section) => (
                  <FormControlLabel
                    key={section.id}
                    control={
                      <Checkbox
                        checked={formData.sections_access.includes(section.id)}
                        onChange={() => toggleSection(section.id)}
                      />
                    }
                    label={section.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.user_id}>
            {editingCollaborator ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Suppression */}
      <Dialog open={openDeleteDialog !== null} onClose={() => setOpenDeleteDialog(null)}>
        <DialogTitle>Supprimer le collaborateur</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer ce collaborateur ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(null)}>Annuler</Button>
          <Button
            onClick={() => openDeleteDialog && handleDelete(openDeleteDialog)}
            variant="contained"
            color="error"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollaboratorsManagement;

